// ─── OnboardingWizard.tsx — Modal de bienvenue post-inscription ───────────────
//
// Affiché une seule fois après l'inscription.
// Flag localStorage : wezea_onboarding_done_{userId}
// Étapes : Bienvenue → Scanner votre domaine → Prochaines étapes
//
import { useState, useEffect, useCallback, FormEvent } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Shield, Globe, Star, ArrowRight, X, CheckCircle,
  Bell, Zap, FileText, Lock,
} from 'lucide-react';
import type { AuthUser } from '../contexts/AuthContext';
import SkuIcon from './SkuIcon';
import { useLanguage } from '../i18n/LanguageContext';

// ─── StepDot ──────────────────────────────────────────────────────────────────
function StepDots({ total, current }: { total: number; current: number }) {
  return (
    <div className="flex items-center gap-2 justify-center">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          style={{
            width: i === current ? 20 : 6,
            height: 6,
            borderRadius: 3,
            background: i === current ? '#0d9488' : 'rgba(255,255,255,0.15)',
            transition: 'all 0.3s ease',
          }}
        />
      ))}
    </div>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────
interface Props {
  user: AuthUser;
  /** Appelé quand l'utilisateur lance un scan depuis le wizard */
  onStartScan: (domain: string) => void;
  /** Appelé quand l'utilisateur clique "Voir mon espace" */
  onGoClientSpace: (tab?: string) => void;
  /** Ferme le wizard sans action */
  onClose: () => void;
}

// ─── Composant principal ──────────────────────────────────────────────────────
export default function OnboardingWizard({ user, onStartScan, onGoClientSpace, onClose }: Props) {
  const { lang } = useLanguage();
  const t = (fr: string, en: string) => (lang === 'en' ? en : fr);
  const [step, setStep] = useState(0);
  const [domain, setDomain] = useState('');
  const [domainError, setDomainError] = useState('');

  // Fermeture par Escape
  const handleEscape = useCallback((e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); }, [onClose]);
  useEffect(() => {
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleEscape]);

  const firstName = user.first_name || user.email.split('@')[0];

  // Validation rapide du domaine
  function validateDomain(d: string): boolean {
    const clean = d.trim().replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0];
    return /^[a-zA-Z0-9][a-zA-Z0-9\-]*(\.[a-zA-Z]{2,})+$/.test(clean);
  }

  function handleScan(e: FormEvent) {
    e.preventDefault();
    setDomainError('');
    const clean = domain.trim().replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0];
    if (!clean || !validateDomain(clean)) {
      setDomainError(t('Entrez un nom de domaine valide (ex: mon-site.fr)', 'Enter a valid domain name (e.g. my-site.com)'));
      return;
    }
    onStartScan(clean);
    onClose();
  }

  const STEPS = [
    {
      key: 'welcome',
      render: () => (
        <motion.div
          key="step-welcome"
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -40 }}
          transition={{ duration: 0.3 }}
          className="flex flex-col items-center text-center gap-6"
        >
          {/* Icône hero */}
          <SkuIcon color="#0d9488" size={72}>
            <Shield size={36} className="text-[var(--color-accent-strong)]" />
          </SkuIcon>

          {/* Titre */}
          <div>
            <h2 className="text-2xl font-bold text-[var(--color-ink)] mb-2" style={{ fontFamily: 'var(--font-display)' }}>
              {t('Bienvenue', 'Welcome')}, {firstName} 👋
            </h2>
            <p className="text-[var(--color-muted)] text-sm leading-relaxed max-w-sm mx-auto">
              {t(
                "Wezea analyse la sécurité de vos domaines et vous aide à corriger les vulnérabilités avant qu'elles ne soient exploitées.",
                'Wezea analyzes the security of your domains and helps you fix vulnerabilities before they are exploited.',
              )}
            </p>
          </div>

          {/* 3 cartes de fonctionnalités */}
          <div className="w-full grid grid-cols-1 gap-3 text-left">
            {[
              { icon: Globe, color: '#0d9488', title: t('Analyse complète', 'Full analysis'), desc: t('SSL, DNS, ports, technologies — un score de sécurité en secondes.', 'SSL, DNS, ports, technologies — a security score in seconds.') },
              { icon: Bell, color: '#a78bfa', title: t('Surveillance continue', 'Continuous monitoring'), desc: t('Soyez alerté dès qu\'une anomalie est détectée sur vos domaines.', 'Get alerted as soon as an anomaly is detected on your domains.') },
              { icon: FileText, color: '#059669', title: t('Rapport PDF expert', 'Expert PDF report'), desc: t('Un rapport professionnel pour vos clients ou votre équipe IT.', 'A professional report for your clients or your IT team.') },
            ].map(({ icon: Icon, color, title, desc }) => (
              <div
                key={title}
                className="flex items-start gap-3 p-3 rounded-lg"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
              >
                <SkuIcon color={color} size={32}>
                  <Icon size={16} style={{ color }} />
                </SkuIcon>
                <div>
                  <div className="text-sm font-semibold text-[var(--color-ink)]">{title}</div>
                  <div className="text-xs text-[var(--color-muted)] leading-snug mt-0.5">{desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <button
            onClick={() => setStep(1)}
            className="sku-btn-primary w-full flex items-center justify-center gap-2 rounded-xl"
            style={{ padding: '12px 24px', fontSize: '0.9rem' }}
          >
            <Zap size={16} />
            {t('Commencer — scanner mon domaine', 'Get started — scan my domain')}
            <ArrowRight size={16} />
          </button>
          <button
            onClick={onClose}
            className="text-xs text-[var(--color-muted)] hover:text-[var(--color-muted)] transition-colors"
          >
            {t("Passer l'introduction", 'Skip the intro')}
          </button>
        </motion.div>
      ),
    },
    {
      key: 'scan',
      render: () => (
        <motion.div
          key="step-scan"
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -40 }}
          transition={{ duration: 0.3 }}
          className="flex flex-col items-center text-center gap-6"
        >
          <SkuIcon color="#0d9488" size={60}>
            <Globe size={28} className="text-[var(--color-accent-strong)]" />
          </SkuIcon>

          <div>
            <h2 className="text-xl font-bold text-[var(--color-ink)] mb-2" style={{ fontFamily: 'var(--font-display)' }}>
              {t('Scannez votre premier domaine', 'Scan your first domain')}
            </h2>
            <p className="text-[var(--color-muted)] text-sm leading-relaxed">
              {t("Entrez le domaine que vous souhaitez analyser. L'analyse prend environ 15 secondes.",
                 'Enter the domain you want to analyze. The scan takes about 15 seconds.')}
            </p>
          </div>

          <form onSubmit={handleScan} className="w-full flex flex-col gap-3">
            <div className="w-full">
              <input
                type="text"
                value={domain}
                onChange={e => { setDomain(e.target.value); setDomainError(''); }}
                placeholder="mon-site.fr"
                autoFocus
                className="sku-inset w-full px-4 py-3 rounded-lg text-[var(--color-ink)] text-sm font-mono placeholder-[var(--color-faint)] outline-none focus:ring-1 focus:ring-[rgba(13,148,136,0.4)]"
              />
              {domainError && (
                <p className="text-xs text-red-500 mt-1.5 text-left">{domainError}</p>
              )}
            </div>

            <button
              type="submit"
              className="sku-btn-primary w-full flex items-center justify-center gap-2 rounded-xl"
              style={{ padding: '12px 24px', fontSize: '0.9rem' }}
            >
              <Shield size={16} />
              {t("Lancer l'analyse", 'Run the scan')}
            </button>
          </form>

          {/* Exemples cliquables */}
          <div className="flex flex-col items-center gap-1">
            <span className="text-xs text-[var(--color-faint)]">{t('Exemples :', 'Examples:')}</span>
            <div className="flex gap-2 flex-wrap justify-center">
              {['exemple.fr', 'mon-shop.com', 'startup.io'].map(ex => (
                <button
                  key={ex}
                  onClick={() => setDomain(ex)}
                  className="text-xs px-2 py-0.5 rounded text-[var(--color-muted)] hover:text-[var(--color-accent-strong)] transition-colors"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>

          {/* Lien vers étape suivante */}
          <button
            onClick={() => setStep(2)}
            className="text-xs text-[var(--color-muted)] hover:text-[var(--color-muted)] transition-colors"
          >
            {t("Je veux d'abord explorer les fonctionnalités →", 'I\'d rather explore the features first →')}
          </button>
        </motion.div>
      ),
    },
    {
      key: 'next',
      render: () => (
        <motion.div
          key="step-next"
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -40 }}
          transition={{ duration: 0.3 }}
          className="flex flex-col items-center text-center gap-6"
        >
          <SkuIcon color="#059669" size={60}>
            <CheckCircle size={28} className="text-emerald-600" />
          </SkuIcon>

          <div>
            <h2 className="text-xl font-bold text-[var(--color-ink)] mb-2" style={{ fontFamily: 'var(--font-display)' }}>
              {t('Vous êtes prêt !', "You're all set!")}
            </h2>
            <p className="text-[var(--color-muted)] text-sm leading-relaxed">
              {t('Votre espace vous attend. Voici ce que vous pouvez faire dès maintenant.',
                 'Your workspace is ready. Here\'s what you can do right now.')}
            </p>
          </div>

          <div className="w-full grid grid-cols-1 gap-3 text-left">
            {[
              {
                icon: Globe, color: '#0d9488',
                title: t('Scanner un domaine', 'Scan a domain'),
                desc: t('Depuis le tableau de bord, entrez n\'importe quel domaine pour obtenir votre score.', 'From the dashboard, enter any domain to get your score.'),
                action: () => { onClose(); },
              },
              {
                icon: Bell, color: '#a78bfa',
                title: t('Activer la surveillance', 'Enable monitoring'),
                desc: t('Recevez des alertes email en cas de changement sur vos domaines.', 'Get email alerts whenever something changes on your domains.'),
                action: () => { onGoClientSpace('monitoring'); onClose(); },
              },
              {
                icon: Star, color: '#f59e0b',
                title: t('Voir les plans', 'See the plans'),
                desc: t('Débloquez les rapports PDF, l\'API et la surveillance illimitée.', 'Unlock PDF reports, the API and unlimited monitoring.'),
                action: () => { onGoClientSpace('billing'); onClose(); },
              },
            ].map(({ icon: Icon, color, title, desc, action }) => (
              <button
                key={title}
                onClick={action}
                className="flex items-start gap-3 p-3 rounded-lg text-left w-full transition-all group"
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.03)')}
              >
                <SkuIcon color={color} size={32}>
                  <Icon size={16} style={{ color }} />
                </SkuIcon>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[var(--color-ink)]">{title}</div>
                  <div className="text-xs text-[var(--color-muted)] leading-snug mt-0.5">{desc}</div>
                </div>
                <ArrowRight size={14} className="text-[var(--color-faint)] group-hover:text-[var(--color-muted)] transition-colors shrink-0 mt-1" />
              </button>
            ))}
          </div>

          <button
            onClick={onClose}
            className="sku-btn-ghost w-full flex items-center justify-center gap-2"
            style={{ padding: '10px 24px', fontSize: '0.85rem' }}
          >
            <Lock size={14} />
            {t('Fermer et explorer', 'Close and explore')}
          </button>
        </motion.div>
      ),
    },
  ];

  return (
    // Overlay
    <motion.div
      role="dialog"
      aria-modal="true"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Panel */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 16 }}
        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
        className="sku-panel relative w-full max-w-md overflow-hidden"
        style={{ padding: '28px 28px 24px' }}
      >
        {/* Bouton fermer */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 flex items-center justify-center rounded-md text-[var(--color-muted)] hover:text-[var(--color-ink)] transition-colors"
          style={{
            width: 28, height: 28,
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
          aria-label={t('Fermer', 'Close')}
        >
          <X size={14} />
        </button>

        {/* Indicateur de progression */}
        <div className="mb-6">
          <StepDots total={STEPS.length} current={step} />
        </div>

        {/* Contenu animé */}
        <AnimatePresence mode="wait">
          {STEPS[step].render()}
        </AnimatePresence>

        {/* Navigation arrière */}
        {step > 0 && (
          <button
            onClick={() => setStep(s => s - 1)}
            className="mt-4 text-xs text-[var(--color-faint)] hover:text-[var(--color-muted)] transition-colors flex items-center gap-1 mx-auto"
          >
            {t('← Étape précédente', '← Previous step')}
          </button>
        )}
      </motion.div>
    </motion.div>
  );
}
